import { useMemo } from "react";
import ReactECharts from "echarts-for-react";

export interface GapAnalysisRow {
  year: number;
  planYear: number;
  percentage: number;
}

interface GapAnalysisChartProps {
  data?: GapAnalysisRow[];
  projectionData?: GapAnalysisRow[];
  showProjections?: boolean;
  isDark?: boolean;
}

const SURPLUS_COLOR = "#22C55E";
const DEFICIT_COLOR = "#EF4444";

const MOCK_ROWS: GapAnalysisRow[] = [
  { year: 2025, planYear: 1, percentage: -25 },
  { year: 2026, planYear: 2, percentage: 40 },
  { year: 2027, planYear: 3, percentage: 51 },
  { year: 2028, planYear: 4, percentage: -52 },
  { year: 2029, planYear: 5, percentage: 70 },
];

const makeHexSymbol = (color: string): string => {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 20 18" fill="none">` +
    `<g clip-path="url(#hclip)">` +
    `<path d="M13.4687 1.26519H13.4729L13.5235 1.26625C14.1466 1.27152 14.7311 1.61259 15.0481 2.15663` +
    `L18.5009 8.08194C18.831 8.64812 18.831 9.35188 18.5009 9.91858L15.0486 15.8439C14.728 16.3937` +
    ` 14.1372 16.7353 13.5068 16.7353H6.49373C5.86282 16.7353 5.27205 16.3937 4.9519 15.8444L3.49975` +
    ` 13.3525L1.49907 9.91911C1.16902 9.35294 1.16902 8.64917 1.49907 8.08247L4.9519 2.15663C5.27205` +
    ` 1.60679 5.86334 1.26519 6.49373 1.26519H13.4687Z" fill="${color}"/>` +
    `</g>` +
    `<defs><clipPath id="hclip"><rect width="20" height="18" fill="white"/></clipPath></defs>` +
    `</svg>`;
  return `image://data:image/svg+xml,${encodeURIComponent(svg)}`;
};

function GapAnalysisChart({
  data,
  projectionData,
  showProjections = false,
  isDark = false,
}: GapAnalysisChartProps) {
  const rows = data && data.length > 0 ? data : MOCK_ROWS;

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const surplusColor = isDark ? "#86efac" : SURPLUS_COLOR;
    const deficitColor = isDark ? "#fca5a5" : DEFICIT_COLOR;
    const axisBorderColor = isDark ? "#3A3A50" : "#d1d5db";
    const projColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(200,200,200,0.12)";
    const projBorderColor = isDark ? "#4A5568" : "#CBD5E1";

    type CombinedRow = GapAnalysisRow & { isProjection: boolean };

    const allRows: CombinedRow[] = [
      ...rows.map((r) => ({ ...r, isProjection: false })),
      ...(showProjections && projectionData && projectionData.length > 0
        ? projectionData.map((r) => ({ ...r, isProjection: true }))
        : []),
    ];

    const maxAbs = Math.max(...allRows.map((r) => Math.abs(r.percentage)), 60);
    const roundedMax = Math.ceil(maxAbs / 20) * 20;

    return {
      animation: false,
      backgroundColor: isDark ? "#2A2A3D" : "#ffffff",
      tooltip: {
        show: true,
        trigger: "item" as const,
        backgroundColor: isDark ? "#1e2030" : "#ffffff",
        borderColor: isDark ? "#3A3A50" : "#E5E7EB",
        textStyle: { color: textColor, fontSize: 12 },
        formatter: (params: any) => {
          if (!params) return "";
          const row = allRows[params.dataIndex];
          if (!row || row.isProjection) return "";
          const isSurplus = row.percentage >= 0;
          const color = isSurplus ? surplusColor : deficitColor;
          const label = isSurplus ? "Surplus" : "Deficit";
          return (
            `<div style="padding:4px 8px">` +
            `<div style="font-weight:600;margin-bottom:4px">${row.year} · Plan Yr ${row.planYear}</div>` +
            `<div style="color:${color};font-weight:700">${label}: ${Math.abs(
              row.percentage,
            )}%</div>` +
            `</div>`
          );
        },
      },
      legend: { show: false },
      grid: { left: 52, right: 24, top: 40, bottom: 36 },
      xAxis: {
        type: "category" as const,
        data: allRows.map((r) => String(r.year)),
        axisLine: { lineStyle: { color: axisBorderColor, width: 1 } },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      yAxis: {
        type: "value" as const,
        min: -roundedMax,
        max: roundedMax,
        interval: 20,
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (v: number) => (v === 0 ? "" : `${Math.abs(v)}%`),
        },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: "bar" as const,
          barMaxWidth: "80",
          barCategoryGap: "8%",
          label: { show: true },
          data: allRows.map((row) => ({
            value: row.percentage,
            itemStyle: row.isProjection
              ? {
                  color: projColor,
                  borderColor: projBorderColor,
                  borderType: "dashed" as const,
                  borderWidth: 1.5,
                }
              : {
                  color:
                    row.percentage >= 0
                      ? isDark
                        ? "rgba(34,197,94,0.40)"
                        : "rgba(134,239,172,0.70)"
                      : isDark
                      ? "rgba(239,68,68,0.35)"
                      : "rgba(252,165,165,0.70)",
                },
            label: row.isProjection
              ? {
                  show: true,
                  position: row.percentage < 0 ? ("top" as const) : ("bottom" as const),
                  distance: 5,
                  formatter: String(row.year),
                  color: isDark ? "#4B5563" : "#9CA3AF",
                  fontSize: 11,
                  fontFamily: "system-ui, sans-serif",
                }
              : {
                  show: true,
                  position: row.percentage >= 0 ? ("top" as const) : ("bottom" as const),
                  distance: 8,
                  formatter: `${Math.abs(row.percentage)}%`,
                  color: row.percentage >= 0 ? surplusColor : deficitColor,
                  fontWeight: 700,
                  fontSize: 13,
                  fontFamily: "system-ui, sans-serif",
                },
          })),
        },
        {
          type: "scatter" as const,
          z: 20,
          silent: true,
          symbolSize: [22, 20],
          symbolKeepAspect: true,
          label: { show: true },
          data: allRows.map((row, i) => ({
            value: [i, 0],
            symbol: row.isProjection
              ? "none"
              : makeHexSymbol(row.percentage >= 0 ? surplusColor : deficitColor),
            label: row.isProjection
              ? { show: false }
              : {
                  show: true,
                  formatter: String(row.year),
                  position: row.percentage < 0 ? ("top" as const) : ("bottom" as const),
                  distance: 5,
                  color: textColor,
                  fontSize: 11,
                  fontFamily: "system-ui, sans-serif",
                },
          })),
        },
      ],
    };
  }, [rows, projectionData, showProjections, isDark]);

  return (
    <div className="mt-2">
      <p className="text-xl conthrax-font mb-1 px-1 text-[#1e2d4d] dark:text-gray-100">
        Gap Analysis
      </p>
      <ReactECharts option={option} style={{ height: 600 }} notMerge lazyUpdate />
    </div>
  );
}

export default GapAnalysisChart;
